import { message } from 'antd'
import { useState } from 'react'
import { boxFromCode } from '@wolf/barcodes'
import { BoxFromBarcode } from '@wolf/barcodes/dist/models'
import { trpc } from '../../../utils/trpc'

type ExtractedBoxData = {
  barcode: string
  boxData: BoxFromBarcode
  pageNumber: number
}

type UsePdfUploadReturn = {
  isUploading: boolean
  uploadedFile: File | null
  extractedBoxes: Array<ExtractedBoxData>
  handleFileUpload: (file: File) => Promise<void>
  clearFile: () => void
}

export const usePdfUpload = (): UsePdfUploadReturn => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [extractedBoxes, setExtractedBoxes] = useState<Array<ExtractedBoxData>>([])

  const extractPdfMutation = trpc.boxes.extractPdfBarcodes.useMutation()

  const handleFileUpload = async (file: File): Promise<void> => {
    // Validate file type
    if (file.type !== 'application/pdf') {
      message.error('Por favor, sube un archivo PDF válido')
      return
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024
    if (file.size > maxSize) {
      message.error('El archivo es demasiado grande. Tamaño máximo: 50MB')
      return
    }

    setIsUploading(true)

    try {
      // Store the file for later processing
      setUploadedFile(file)

      const arrayBuffer = await file.arrayBuffer()
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      )

      const { barcodes } = await extractPdfMutation.mutateAsync({ pdfBase64: base64 })

      // Check for duplicate barcodes
      const duplicateBarcodes = barcodes.filter((barcode, index) => barcodes.indexOf(barcode) !== index)
      if (duplicateBarcodes.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateBarcodes)]
        message.error(`Error: Se encontraron códigos de barras duplicados: ${uniqueDuplicates.join(', ')}`)
        setUploadedFile(null)
        setExtractedBoxes([])
        return
      }

      const extractedData: Array<ExtractedBoxData> = []
      barcodes.forEach((barcode, index) => {
        try {
          const boxData = boxFromCode(barcode)
          extractedData.push({
            barcode,
            boxData,
            pageNumber: index + 1
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(`Invalid barcode found on page ${index + 1}: "${barcode}"`, error)
        }
      })

      // Validate pageNumbers are consecutive (no gaps)
      const pageNumbers = extractedData.map(box => box.pageNumber).sort((a, b) => a - b)
      for (let i = 0; i < pageNumbers.length; i++) {
        if (pageNumbers[i] !== i + 1) {
          message.error(`Error: Faltan páginas en la secuencia. Se esperaba página ${i + 1} pero se encontró ${pageNumbers[i]}`)
          setUploadedFile(null)
          setExtractedBoxes([])
          return
        }
      }

      setExtractedBoxes(extractedData)

      if (extractedData.length > 0) {
        message.success(`Archivo procesado: ${extractedData.length} código(s) de barras encontrado(s)`)
      } else {
        message.warning('No se encontraron códigos de barras válidos entre # en el PDF')
      }
    } catch (error) {
      message.error('Error al procesar el archivo PDF')
      setUploadedFile(null)
      setExtractedBoxes([])
    } finally {
      setIsUploading(false)
    }
  }

  const clearFile = () => {
    setUploadedFile(null)
    setExtractedBoxes([])
  }

  return {
    isUploading,
    uploadedFile,
    extractedBoxes,
    handleFileUpload,
    clearFile
  }
}
